import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, PlusCircle, Map, RefreshCw, ArrowLeftRight } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Eyebrow, PageTitle, Loader, EmptyState, Card, BackHeader, TripRoute } from "@/components/common";
import { cn } from "@/lib/utils";

export default function TripsPage() {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [tab, setTab] = useState("tutti");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterVehicle, setFilterVehicle] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [sortBy, setSortBy] = useState("rilevanza");

  const isCarrier = user?.account_type === "vettore";

  useEffect(() => {
    apiFetch("/trips").then(setTrips).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const from = filterFrom.trim().toLowerCase();
    const to = filterTo.trim().toLowerCase();
    const vehicle = filterVehicle.trim().toLowerCase();
    const date = filterDate.trim().toLowerCase();
    const result = trips.filter((t) => {
      if (!`${t.pickup} ${t.delivery} ${t.vehicle_type} ${t.cargo}`.toLowerCase().includes(query.toLowerCase())) return false;
      if (from && !(t.pickup || "").toLowerCase().includes(from)) return false;
      if (to && !(t.delivery || "").toLowerCase().includes(to)) return false;
      if (vehicle && !(t.vehicle_type || "").toLowerCase().includes(vehicle)) return false;
      if (date && !(t.date || "").toLowerCase().includes(date)) return false;
      if (isCarrier && tab === "perte" && !t.recommended) return false;
      return true;
    });
    if (sortBy === "prezzo") {
      result.sort((a, b) => (b.price_proposed ?? -1) - (a.price_proposed ?? -1));
    } else if (sortBy === "data") {
      result.sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
    }
    return result;
  }, [trips, query, filterFrom, filterTo, filterVehicle, filterDate, sortBy, tab, isCarrier]);

  const inputCls = "min-h-[46px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-sm outline-none focus:border-primary";

  return (
    <div className="space-y-4">
      <BackHeader />
      <div className="flex items-start justify-between">
        <div>
          <Eyebrow>MERCATO</Eyebrow>
          <PageTitle>Viaggi disponibili</PageTitle>
        </div>
        {user?.account_type !== "autista" ? (
          <Link to="/publish-trip" data-testid="trips-publish-button" className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <PlusCircle className="h-6 w-6" />
          </Link>
        ) : null}
      </div>

      <div className="flex min-h-[52px] items-center gap-2.5 rounded-2xl border border-border bg-card px-4">
        <Search className="h-5 w-5 text-muted-foreground" />
        <input
          data-testid="trips-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca tratta, merce o mezzo"
          className="h-12 flex-1 bg-transparent text-[15px] outline-none"
        />
      </div>

      {isCarrier ? (
        <div className="flex gap-2">
          {[["perte", "Per te"], ["tutti", "Tutti"]].map(([value, label]) => (
            <button
              key={value}
              data-testid={`trips-tab-${value}`}
              onClick={() => setTab(value)}
              className={cn(
                "flex-1 rounded-full px-4 py-2.5 text-sm font-extrabold transition-colors",
                tab === value ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
              )}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <button
          data-testid="trips-filter-toggle"
          onClick={() => setShowFilters((v) => !v)}
          className={cn(
            "flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold",
            showFilters ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
          )}
        >
          <SlidersHorizontal className="h-4 w-4" /> Filtri
        </button>
        <span className="text-xs text-muted-foreground">{filtered.length} risultati</span>
      </div>

      {showFilters ? (
        <Card className="space-y-2.5">
          <input data-testid="trips-filter-from" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} placeholder="Partenza (luogo di carico)" className={inputCls} />
          <input data-testid="trips-filter-to" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} placeholder="Arrivo (luogo di scarico)" className={inputCls} />
          <input data-testid="trips-filter-vehicle" value={filterVehicle} onChange={(e) => setFilterVehicle(e.target.value)} placeholder="Mezzo (es. frigo, centinato)" className={inputCls} />
          <input data-testid="trips-filter-date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} placeholder="Data (es. domani, 18 ott)" className={inputCls} />
          <select data-testid="trips-filter-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={inputCls}>
            <option value="rilevanza">Ordina per: Rilevanza</option>
            <option value="data">Ordina per: Data di carico</option>
            <option value="prezzo">Ordina per: Prezzo</option>
          </select>
          <button data-testid="trips-filter-reset" onClick={() => { setFilterFrom(""); setFilterTo(""); setFilterVehicle(""); setFilterDate(""); setSortBy("rilevanza"); }} className="flex items-center gap-1.5 text-sm font-extrabold text-primary">
            <RefreshCw className="h-4 w-4" /> Azzera filtri
          </button>
        </Card>
      ) : null}

      {loading ? (
        <Loader />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Map} title="Nessun viaggio trovato" text="Prova a modificare la ricerca o pubblica il tuo primo viaggio." />
      ) : (
        <div className="space-y-3">
          {filtered.map((trip) => (
            <Link key={trip.id} to={`/trip/${trip.id}`} data-testid={`trip-card-${trip.id}`}>
              <Card className="space-y-3 hover:shadow-md">
                <TripRoute trip={trip} />
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>{trip.date}{trip.time ? ` · ${trip.time}` : ""}</span>
                  <span>· {trip.vehicle_type}</span>
                </div>
                {trip.recommended ? (
                  <span data-testid={`trip-recommended-${trip.id}`} className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-extrabold text-primary-foreground">
                    <Map className="h-3.5 w-3.5" /> Consigliato per te
                  </span>
                ) : null}
                {trip.parent_trip_id ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-extrabold text-secondary-foreground">
                    <ArrowLeftRight className="h-3.5 w-3.5" /> Cessione tra vettori
                  </span>
                ) : null}
                <div className="border-t border-border pt-2.5">
                  <p className="text-sm font-bold text-card-foreground">{trip.cargo || "Trasporto merci"}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded-lg bg-secondary/60 px-2.5 py-1 text-xs text-muted-foreground">{trip.weight || "Peso da definire"}</span>
                    {trip.pallets ? <span data-testid={`trip-pallets-${trip.id}`} className="rounded-lg bg-secondary/60 px-2.5 py-1 text-xs text-muted-foreground">{trip.pallets} pallet</span> : null}
                    <span className="rounded-lg bg-secondary/60 px-2.5 py-1 text-xs text-muted-foreground">{trip.requirements?.join(" · ") || "Requisiti standard"}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="truncate text-xs text-muted-foreground">{trip.owner_name}</span>
                  {trip.price_proposed != null ? <span className="text-base font-extrabold text-primary">€ {trip.price_proposed}</span> : null}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
